<template>
  <CardWrapper
    class="last-transactions-widget"
    data-test-id="last-transactions-widget"
    :title="i18n.dash_activity()"
  >
    <template #title>
      <div class="title-btns">
        <RoundButton
          size="sm"
          testId="last-transactions-widget-refresh-btn"
          icon="autorenew"
          @click="refresh"
        />
        <md-button
          v-if="resultList.length"
          to="/history/index"
          :disabled="accountState !== 'approved'"
          class="simple-link arrow"
          data-test-id="last-transactions-widget-history-link"
        >
          {{ i18n.lbl_all_history() }}
        </md-button>
      </div>
    </template>
    <div
      data-test-id="last-transactions-widget-table-wrapper"
      class="last-transactions-widget__table-wrapper"
    >
      <table
        v-if="resultList.length"
        class="last-transactions-widget__table"
        data-test-id="last-transactions-widget-table"
      >
        <thead data-test-id="last-transactions-widget-table-header">
          <tr>
            <th>{{ i18n.operation_type() }}</th>
            <th>{{ i18n.lbl_counterparty() }}</th>
            <th>{{ i18n.lbl_date() }}</th>
            <th>{{ i18n.lbl_amount() }}</th>
            <th>{{ i18n.lbl_status() }}</th>
          </tr>
        </thead>
        <tbody data-test-id="last-transactions-widget-table-body">
          <tr
            v-for="(tx, i) in resultList"
            :key="i"
            :class="[
              { 'row_opacity': checkRowForFailed(tx) },
              { 'row_gray': checkGrayRow(tx) }
            ]"
            :data-test-id="`last-transactions-widget-table-row-${i}`"
            @click="makeDialogIsShow(tx)"
          >
            <td
              class="text_bold operation-type"
              data-test-id="last-transactions-widget-table-type-cell"
            >
              {{ tx.name }}
            </td>
            <td
              class="counterparty text-ellipsis"
              data-test-id="last-transactions-widget-table-counterparty-cell"
            >
              {{ tx.counterparty ? tx.counterparty : '-' }}
            </td>
            <td data-test-id="last-transactions-widget-table-date-cell">
              {{ tx.date }}
            </td>
            <td
              :class="[
                'amount',
                `amount_${tx.direction}`,
                tx.state === 'pending' ? 'amount_pending' : '',
                checkRowForFailed(tx) ? 'amount_failed' : ''
              ]"
              data-test-id="last-transactions-widget-table-amount-cell"
            >
              <md-tooltip
                md-direction="top"
                class="custom-menu-tooltip"
                data-test-id="last-transactions-widget-table-fees-tooltip"
              >
                <span>{{ i18n.lbl_fees_label() }}</span>
                {{ i18n.c(swimAmount(tx.asset, getTotalFees(tx))) }} {{ formatAsset(tx.asset) }}
              </md-tooltip>
              {{ tx.direction === 'in' ? '+' : '-' }}
              {{ swimAmount(tx.asset, tx.amount) }}
              {{ formatAsset(tx.asset) }}
            </td>
            <td
              :class="['status', tx.state]"
              data-test-id="last-transactions-widget-table-status-cell"
            >
              <span class="status__indicator"></span>
              <span class="status__text">{{ i18n.tx_state(tx.state) }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-else
        class="last-transactions-widget__empty text-center"
        data-test-id="last-transactions-widget-empty-table"
      >
        <md-icon
          class="md-size-4x"
          data-test-id="last-transactions-widget-empty-table-icon"
        >
          trending_up
        </md-icon>
        <div
          class="text_xl_large"
          data-test-id="last-transactions-widget-empty-table-title"
        >
          {{ i18n.th_no_transaction_history() }}
        </div>
        <div
          class="text_margin text_medium"
          data-test-id="last-transactions-widget-empty-table-msg"
        >
          <span>{{ i18n.th_no_transaction_history_search() }}</span>
          <span
            class="contact-us__link"
            data-test-id="last-transactions-widget-empty-table-dialog-link"
            @click="showContactDialog = true"
          >
            {{ i18n.lbl_contact_us() }}
          </span>
          <span>{{ i18n.th_no_transaction_history_search_end() }}</span>
        </div>
      </div>
    </div>
    <info-dialog
      v-if="showInfoDialog"
      :dialogValues="dialogValues"
      :showDialog="showInfoDialog"
      data-test-id="last-transactions-widget-info-dialog"
      @close-dialog="closeDialog"
    />
    <contact-us
      v-if="showContactDialog"
      class="contact-us__popup"
      data-test-id="last-transactions-widget-contact-dialog"
      :showDialog="showContactDialog"
      @close-dialog="showContactDialog = false"
    />
  </CardWrapper>
</template>

<script>
  import { i18n } from '@js/i18n'
  import { mapGetters, mapActions } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { RecordTypes } from '@js/records/types'
  import get from 'lodash/get'
  import {isCryptoAsset, formatTokenDisplay} from '@utils/consts'
  import { CurrencyHelper } from '@js/helpers/currency.helper'

  import CurrencyRound from '@com/mixins/currency.round.mixin'
  import CardWrapper from '@com/UI/CardWrapper.vue'
  import RoundButton from '@com/UI/RoundBtn.vue'
  import InfoDialog from '../index/Dashboard.InfoDialog.vue'
  import ContactUs from '../../../navigation/contact-us_popup/contact-us'

  export default {
    name: 'LastTransactionsWidget',
    mixins: [CurrencyRound],
    components: {
      CardWrapper,
      RoundButton,
      InfoDialog,
      ContactUs
    },
    props: ['currentAsset'],
    data () {
      return {
        i18n,
        showInfoDialog: false,
        showContactDialog: false,
        dialogValues: {}
      }
    },
    computed: {
      ...mapGetters([
        vuexTypes.accountState,
        vuexTypes.transactions
      ]),
      transactionsList () {
        return get(this.transactions, `all.records`) || []
      },
      resultList () {
        return this.transactionsList
          .reduce((list, item) => {
            if (item instanceof RecordTypes.MatchRecord) {
              item.transactions.forEach(tx => { list.push(tx) })
              return list
            }
            list.push(item)
            return list
          }, [])
          .slice(0, 10)
      }
    },
    methods: {
      ...mapActions({
        loadList: vuexTypes.GET_TX_LIST,
        loadUsersDetails: vuexTypes.UPDATE_TX_COUNTERPARTIES
      }),
      checkRowForFailed (tx) {
        return tx.state === 'rejected' || tx.state === 'failed'
      },
      checkGrayRow (tx) {
        if (!tx.asset || typeof tx.asset !== 'string') return false
        return tx.asset.endsWith('20D')
      },
      refresh () {
        this.loadList({risk_type: 'all', code: '', limit: 10})
      },
      formatAsset (token) {
        if (isCryptoAsset(token)) {
          return formatTokenDisplay(token)
        }
        return CurrencyHelper.getCurrencyIcon(token)
      },
      swimAmount (token, amount) {
        if (isCryptoAsset(token)) {
          return this.roundCurrency2(amount.toString(), token)
        }
        return this.roundCurrency(amount, token)
      },
      makeDialogIsShow (list) {
        this.showInfoDialog = true
        this.dialogValues = list
      },
      closeDialog (status) {
        this.showInfoDialog = status
        this.dialogValues = {}
      },
      getTotalFees (tx) {
        if (tx.percentFee && tx.fixedFee) {
          return parseFloat(tx.percentFee) + parseFloat(tx.fixedFee)
        } else if (tx.fees) {
          return parseFloat(tx.fees.source) + parseFloat(tx.fees.destination)
        }
        return 0
      }
    },
    watch: {
      currentAsset (value) {
        this.loadList({risk_type: 'all', code: '', limit: 10})
      },
      transactionsList (newValue) {
        if (!newValue || !newValue.length) return

        this.loadUsersDetails(newValue)
      }
    },
    created () {
      this.loadList({risk_type: 'all', code: '', limit: 10})
    }
  }
</script>

<style scoped lang="scss">
@import '~scss/variables';

.last-transactions-widget {
  .title-btns {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;

    .simple-link {
      display: flex;
      align-items: center;

      &.md-button.arrow {
        &:after {
          color: $color-mint;
          top: 4.5px;
        }
      }

      &:hover {
        color: $color-mint;
      }
    }
  }

  &__table-wrapper {
    margin-top: 15px;

    &:has(.last-transactions-widget__empty) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: calc(100% - 44px);
    }
  }

  &__table {
    width: 100%;
    border-collapse: collapse;

    thead {
      background-color: $color-mint;
      border-radius: 10px;

      th {
        border-bottom: none;
        padding: 10px 5px;
        font-weight: 600;
        color: $col-text;

        &:first-child {
          padding-left: 10px;
          padding-right: 5px;
        }

        &:last-child {
          padding-left: 5px;
          padding-right: 10px;
        }
      }

      tr:first-child th:first-child {
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
      }

      tr:first-child th:last-child {
        border-top-right-radius: 10px;
        border-bottom-right-radius: 10px;
      }
    }

    th,
    td {
      padding: 15px 5px;
      text-align: left;

      &:first-child {
        padding-left: 10px;
        padding-right: 5px;
      }

      &:last-child {
        padding-left: 5px;
        padding-right: 10px;
      }
    }

    tbody {
      tr {
        border-bottom: 1px solid $grey;
        box-sizing: border-box;

        &:last-child {
          border-bottom: none;
        }

        &.row_opacity { }

        &.row_gray {
          background-color: $gray_asset;
        }

        &:hover {
          background-color: rgba($color-mint, 0.7);
          cursor: pointer;
        }
      }

      .operation-type {
        font-size: 16px;
      }

      .counterparty {
        max-width: 330px;
      }

      .amount {
        font-weight: 600;
        font-size: 16px;

        &_in { color: $col-success; }
        &_out { color: $col-error; }
        &_pending { color: orange; }
        &_failed { color: $grey; }
      }

      .status {
        font-size: 16px;
        font-weight: 600;
        position: relative;
        display: flex;
        align-items: center;

        &__text {
          text-transform: capitalize;
        }

        &__indicator {
          display: inline-block;
          width: 18px;
          height: 18px;
          padding: 3px;
          border-radius: 50%;
          border: 1px solid $col-border;
          transform: translateY(calc(50% - 9px));
          margin-right: 8px;
          position: relative;
          box-sizing: border-box;

          &::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-49%, -49%);
            width: 11px;
            height: 11px;
            border-radius: 50%;
          }
        }

        &.success {
          .status__indicator::before {
            background: radial-gradient(50% 50% at 50% 50%, #00FF2F 0%, $col-success 100%);
            box-shadow: 0px 0px 3px 0px $col-success;
          }
        }

        &.pending {
          .status__indicator::before {
            background: radial-gradient(50% 50% at 50% 50%, #FFEA00 0%, orange 100%);
            box-shadow: 0px 0px 3px 0px orange;
          }
        }

        &.fully,
        &.matched,
        &.fully_matched {
          .status__indicator::before {
            background-color: $col-disabled;
          }
        }

        &.rejected,
        &.failed {
          .status__indicator::before {
            background: radial-gradient(50% 50% at 50% 50%, #FF0000 0%, $col-invalid 100%);
            box-shadow: 0px 0px 3px 0px $col-invalid;
          }
        }
      }
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 75%;

    .contact-us__link {
      text-transform: lowercase;
      border-bottom: 2px solid $color-mint;
      cursor: pointer;
      color: $color-mint;
      transition: border-bottom 0.1s ease-in-out;

      &:hover {
        border-bottom: 2px solid transparent;
      }
    }
  }
}

.text-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}
</style>



// WEBPACK FOOTER //
// src/vue/app/dashboard/widgets/LastTransactionsWidget.vue